/**
 * Frontend Internationalization (i18n) System
 *
 * This file provides client-side translations for React components.
 *
 * Why this exists:
 * - Rails i18n (config/locales/*.yml) only works server-side
 * - React components run in the browser and need JavaScript-based translations
 * - Inertia.js pages are client-side rendered and can't access Rails I18n.t()
 *
 * The locale is initialized from the backend via Inertia shared props and stored
 * in localStorage for persistence across page loads.
 */

type Translations = Record<string, string | Record<string, unknown>>

const translations: Record<string, Translations> = {
  "es-MX": {
    // Navigation
    "nav.dashboard": "Panel",
    "nav.settings": "Configuración",
    "nav.sign_out": "Cerrar sesión",

    // Actions
    "actions.save": "Guardar",
    "actions.cancel": "Cancelar",
    "actions.delete": "Eliminar",
    "actions.edit": "Editar",
    "actions.create": "Crear",
    "actions.search": "Buscar",
    "actions.filter": "Filtrar",
    "actions.add": "Agregar",
    "actions.back": "Volver",

    // Labels
    "labels.name": "Nombre",
    "labels.email": "Correo electrónico",
    "labels.phone": "Teléfono",
    "labels.description": "Descripción",
    "labels.price": "Precio",
    "labels.stock": "Inventario",
    "labels.category": "Categoría",
    "labels.notes": "Notas",
    "labels.actions": "Acciones",

    // Dashboard
    "dashboard.title": "Panel de control",
    "dashboard.subtitle": "Bienvenido a tu panel de control.",
    "dashboard.welcome": "Bienvenido",
    "dashboard.welcome_message": "Tu panel de control está listo. Comienza a construir tu aplicación.",

    // Landing
    "landing.badge": "Rails + React + TypeScript",
    "landing.hero.title": "Construye Aplicaciones Web Modernas",
    "landing.hero.title_highlight": "Más Rápido",
    "landing.hero.description": "Un starter kit listo para producción que combina el poder de Ruby on Rails con la flexibilidad de React. Autenticación, gestión de usuarios y componentes UI modernos incluidos.",
    "landing.hero.get_started": "Comenzar",
    "landing.hero.sign_in": "Iniciar sesión",
    "landing.hero.go_to_dashboard": "Ir al Panel",
    "landing.features.title": "Todo lo que Necesitas",
    "landing.features.subtitle": "Construido con las mejores prácticas modernas y características listas para producción",
    "landing.features.fast.title": "Ultrarrrápido",
    "landing.features.fast.description": "Construido con Vite y React para desarrollo y compilaciones de producción ultrarrrápidas.",
    "landing.features.secure.title": "Seguro por Defecto",
    "landing.features.secure.description": "Autenticación, autorización y mejores prácticas de seguridad integradas desde el primer día.",
    "landing.features.modern.title": "Stack Moderno",
    "landing.features.modern.description": "Rails, React, TypeScript, Inertia.js y TailwindCSS trabajando en perfecta armonía.",
    "landing.features.production.title": "Listo para Producción",
    "landing.features.production.description": "Incluye gestión de usuarios, sesiones, verificación de email y control de acceso basado en roles.",
    "landing.footer.copyright": "Social Toolkit",
    "landing.footer.github": "GitHub",
    "landing.footer.contact": "Contacto",

    // Pricing
    "pricing.title": "Planes y Precios",
    "pricing.subtitle": "Elige el plan perfecto para tus necesidades",
    "pricing.per_month": "/mes",
    "pricing.per_year": "/año",
    "pricing.one_time": "pago único",
    "pricing.popular": "Más popular",
    "pricing.get_started": "Comenzar",
    "pricing.secure_payments": "Pagos seguros procesados por Stripe",

    // Sidebar
    "sidebar.platform": "Plataforma",
    "sidebar.admin": "Administrador",
    "sidebar.plans": "Planes",
    "sidebar.searches": "Búsquedas",
    "sidebar.insights": "Insights",
    "sidebar.billing": "Facturación",

    // Admin Plans
    "admin.plans.title": "Planes de Pago",
    "admin.plans.subtitle": "Administra los planes de suscripción y precios",
    "admin.plans.new": "Nuevo Plan",
    "admin.plans.list": "Lista de Planes",
    "admin.plans.list_description": "Todos los planes de pago configurados",
    "admin.plans.empty": "No hay planes configurados. Crea tu primer plan.",
    "admin.plans.name": "Nombre",
    "admin.plans.price": "Precio",
    "admin.plans.interval": "Intervalo",
    "admin.plans.status": "Estado",
    "admin.plans.stripe_id": "Stripe Price ID",
    "admin.plans.actions": "Acciones",
    "admin.plans.active": "Activo",
    "admin.plans.inactive": "Inactivo",
    "admin.plans.monthly": "Mensual",
    "admin.plans.yearly": "Anual",
    "admin.plans.one_time": "Pago único",
    "admin.plans.delete_confirm_title": "¿Eliminar plan?",
    "admin.plans.delete_confirm_description": "¿Estás seguro de que deseas eliminar el plan {name}? Esta acción no se puede deshacer.",
    "admin.plans.edit": "Editar Plan",
    "admin.plans.create": "Crear Plan",
    "admin.plans.description": "Descripción",
    "admin.plans.stripe_price_id": "Stripe Price ID",
    "admin.plans.stripe_product_id": "Stripe Product ID",
    "admin.plans.price_cents": "Precio (centavos)",
    "admin.plans.currency": "Moneda",
    "admin.plans.position": "Posición",
    "admin.plans.features": "Características (una por línea)",
    "admin.plans.save": "Guardar Plan",
    "sidebar.repository": "Repositorio",
    "sidebar.documentation": "Documentación",

    // Email
    "mailer.footer": "¿Tienes preguntas? Simplemente responde a este correo y nuestro equipo de soporte te ayudará.",
    "mailer.password_reset.subject": "Restablece tu contraseña",
    "mailer.email_verification.subject": "Verifica tu correo electrónico",

    // Settings
    "settings.title": "Configuración",
    "settings.subtitle": "Administra tu perfil y configuración de cuenta",
    "settings.profile": "Perfil",
    "settings.email": "Correo electrónico",
    "settings.password": "Contraseña",
    "settings.mcp_token": "Token MCP",
    "settings.sessions": "Sesiones",
    "settings.billing": "Facturación",
    "settings.appearance": "Apariencia",
    "settings.profile.title": "Información del perfil",
    "settings.profile.subtitle": "Actualiza tu nombre y preferencias de idioma",
    "settings.profile.name": "Nombre",
    "settings.profile.name_placeholder": "Nombre completo",
    "settings.profile.locale": "Idioma",
    "settings.profile.locale_es": "Español (México)",
    "settings.profile.locale_en": "English (US)",
    "settings.profile.save": "Guardar",
    "settings.profile.saved": "Guardado",
    "settings.email.title": "Actualizar correo",
    "settings.email.subtitle": "Actualiza tu correo electrónico y verifícalo",
    "settings.email.label": "Correo electrónico",
    "settings.email.placeholder": "Correo electrónico",
    "settings.email.current_password": "Contraseña actual",
    "settings.email.current_password_placeholder": "Contraseña actual",
    "settings.email.unverified": "Tu correo electrónico no está verificado.",
    "settings.email.resend": "Haz clic aquí para reenviar el correo de verificación.",
    "settings.email.save": "Guardar",
    "settings.email.saved": "Guardado",
    "settings.password.title": "Actualizar contraseña",
    "settings.password.subtitle": "Asegúrate de que tu cuenta use una contraseña larga y aleatoria para mantenerse segura",
    "settings.password.current": "Contraseña actual",
    "settings.password.current_placeholder": "Contraseña actual",
    "settings.password.new": "Nueva contraseña",
    "settings.password.new_placeholder": "Nueva contraseña",
    "settings.password.confirm": "Confirmar contraseña",
    "settings.password.confirm_placeholder": "Confirmar contraseña",
    "settings.password.save": "Guardar contraseña",
    "settings.password.saved": "Guardado",
    "settings.sessions.title": "Sesiones",
    "settings.sessions.subtitle": "Administra tus sesiones activas en todos tus dispositivos",
    "settings.sessions.current": "Sesión activa",
    "settings.sessions.ip": "IP",
    "settings.sessions.active_since": "Activo desde",
    "settings.sessions.log_out": "Cerrar sesión",
    "settings.appearance.title": "Configuración de apariencia",
    "settings.appearance.subtitle": "Actualiza la configuración de apariencia de tu cuenta",
    "settings.appearance.theme": "Tema",
    "settings.appearance.light": "Claro",
    "settings.appearance.dark": "Oscuro",
    "settings.appearance.system": "Sistema",
    "settings.delete.title": "Eliminar cuenta",
    "settings.delete.subtitle": "Elimina tu cuenta y todos sus recursos",
    "settings.delete.button": "Eliminar cuenta",
    "settings.delete.warning": "Advertencia",
    "settings.delete.warning_text": "Por favor procede con precaución, esto no se puede deshacer.",
    "settings.delete.dialog_title": "¿Estás seguro de que quieres eliminar tu cuenta?",
    "settings.delete.dialog_description": "Una vez que tu cuenta sea eliminada, todos sus recursos y datos también serán eliminados permanentemente. Por favor ingresa tu contraseña para confirmar que deseas eliminar tu cuenta permanentemente.",
    "settings.delete.password_label": "Contraseña",
    "settings.delete.password_placeholder": "Contraseña",
    "settings.delete.cancel": "Cancelar",
    "settings.billing.title": "Facturación y Suscripción",
    "settings.billing.subtitle": "Administra tu suscripción e historial de pagos",
    "settings.billing.current_plan": "Plan Actual",
    "settings.billing.current_plan_description": "Tu plan de suscripción activo",
    "settings.billing.payment_history": "Historial de Pagos",
    "settings.billing.payment_history_description": "Ver tus pagos anteriores y facturas",
    "settings.billing.no_plan": "No tienes un plan activo",
    "settings.billing.view_plans": "Ver Planes Disponibles",
    "settings.billing.manage_billing": "Administrar Facturación",
    "settings.billing.cancel_subscription": "Cancelar Suscripción",
    "settings.billing.cancel_confirm": "¿Estás seguro de que deseas cancelar tu suscripción? Seguirás teniendo acceso hasta el final de tu período de facturación.",
    "settings.billing.cancel_success": "Tu suscripción ha sido programada para cancelación al final del período de facturación.",
    "settings.billing.cancel_error": "Error al cancelar la suscripción. Por favor intenta de nuevo.",
    "settings.billing.no_subscription": "No tienes una suscripción activa para cancelar.",
    "settings.billing.subscription_canceled": "Tu suscripción será cancelada al final del período de facturación.",
    "settings.billing.cancel_failed": "Error al cancelar la suscripción: %{error}",
    "settings.billing.payment": "Pago",
    "settings.billing.no_payments": "No hay historial de pagos disponible",
    "settings.billing.monthly": "por mes",
    "settings.billing.yearly": "por año",
    "settings.billing.one_time": "pago único",
    "settings.billing.stripe_info": "La facturación se administra a través de Stripe. Haz clic en 'Administrar Facturación' para actualizar métodos de pago o ver facturas.",
    "settings.billing.status.active": "activo",
    "settings.billing.status.succeeded": "exitoso",
    "settings.billing.status.canceled": "cancelado",
    "settings.billing.status.past_due": "vencido",
    "settings.billing.status.incomplete": "incompleto",
    "settings.billing.status.canceling": "cancelando",
    "settings.billing.status.one_time": "pago único",
    "settings.mcp_token.title": "Token MCP",
    "settings.mcp_token.subtitle": "Administra tu token de acceso para MCP",
    "settings.mcp_token.your_token": "Tu Token",
    "settings.mcp_token.show": "Mostrar",
    "settings.mcp_token.hide": "Ocultar",
    "settings.mcp_token.copy": "Copiar",
    "settings.mcp_token.copied": "¡Copiado!",
    "settings.mcp_token.regenerate": "Regenerar Token",
    "settings.mcp_token.regenerated": "Token regenerado exitosamente",
    "settings.mcp_token.regenerate_confirm": "¿Estás seguro? Tu token anterior dejará de funcionar inmediatamente.",
    "settings.mcp_token.last_access": "Último acceso",
    "settings.mcp_token.never_used": "Nunca usado",
    "settings.mcp_token.just_now": "Justo ahora",
    "settings.mcp_token.minutes_ago": "Hace %{count} minutos",
    "settings.mcp_token.hours_ago": "Hace %{count} horas",
    "settings.mcp_token.days_ago": "Hace %{count} días",
    "settings.mcp_token.how_to_use": "Cómo usar tu token MCP",
    "settings.mcp_token.instructions": "Usa este token para conectar Claude, Cursor u otros clientes MCP a tu cuenta. Agrega la URL a continuación en tu cliente MCP:",

    // Auth
    "auth.sign_in": "Iniciar sesión",
    "auth.sign_up": "Registrarse",
    "auth.sign_out": "Cerrar sesión",
    "auth.forgot_password": "¿Olvidaste tu contraseña?",
    "auth.no_account": "¿No tienes cuenta?",
    "auth.have_account": "¿Ya tienes cuenta?",
    "auth.login.title": "Inicia sesión en tu cuenta",
    "auth.login.description": "Ingresa tu correo y contraseña para iniciar sesión",
    "auth.login.email": "Correo electrónico",
    "auth.login.email_placeholder": "correo@ejemplo.com",
    "auth.login.password": "Contraseña",
    "auth.login.password_placeholder": "Contraseña",
    "auth.login.button": "Iniciar sesión",
    "auth.signup.title": "Crea una cuenta",
    "auth.signup.description": "Ingresa tus datos para crear tu cuenta",
    "auth.signup.name": "Nombre",
    "auth.signup.name_placeholder": "Nombre completo",
    "auth.signup.email": "Correo electrónico",
    "auth.signup.email_placeholder": "correo@ejemplo.com",
    "auth.signup.password": "Contraseña",
    "auth.signup.password_placeholder": "Contraseña",
    "auth.signup.password_confirmation": "Confirmar contraseña",
    "auth.signup.password_confirmation_placeholder": "Confirmar contraseña",
    "auth.signup.button": "Crear cuenta",
    "auth.password_reset.title": "Olvidé mi contraseña",
    "auth.password_reset.description": "Ingresa tu correo para recibir un enlace de recuperación",
    "auth.password_reset.email": "Correo electrónico",
    "auth.password_reset.email_placeholder": "correo@ejemplo.com",
    "auth.password_reset.button": "Enviar enlace de recuperación",
    "auth.password_reset.return": "O, regresar a",
    "auth.password_reset.login": "iniciar sesión",

  },
  "en-US": {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.settings": "Settings",
    "nav.sign_out": "Sign out",

    // Actions
    "actions.save": "Save",
    "actions.cancel": "Cancel",
    "actions.delete": "Delete",
    "actions.edit": "Edit",
    "actions.create": "Create",
    "actions.search": "Search",
    "actions.filter": "Filter",
    "actions.add": "Add",
    "actions.back": "Back",

    // Labels
    "labels.name": "Name",
    "labels.email": "Email",
    "labels.phone": "Phone",
    "labels.description": "Description",
    "labels.price": "Price",
    "labels.stock": "Stock",
    "labels.category": "Category",
    "labels.notes": "Notes",
    "labels.actions": "Actions",

    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.subtitle": "Welcome to your dashboard.",
    "dashboard.welcome": "Welcome",
    "dashboard.welcome_message": "Your dashboard is ready. Start building your application.",

    // Landing
    "landing.badge": "Rails + React + TypeScript",
    "landing.hero.title": "Build Modern Web Apps",
    "landing.hero.title_highlight": "Faster",
    "landing.hero.description": "A production-ready starter kit combining the power of Ruby on Rails with the flexibility of React. Authentication, user management, and modern UI components included.",
    "landing.hero.get_started": "Get Started",
    "landing.hero.sign_in": "Sign In",
    "landing.hero.go_to_dashboard": "Go to Dashboard",
    "landing.features.title": "Everything You Need",
    "landing.features.subtitle": "Built with modern best practices and production-ready features",
    "landing.features.fast.title": "Lightning Fast",
    "landing.features.fast.description": "Built with Vite and React for blazing fast development and production builds.",
    "landing.features.secure.title": "Secure by Default",
    "landing.features.secure.description": "Authentication, authorization, and security best practices built-in from day one.",
    "landing.features.modern.title": "Modern Stack",
    "landing.features.modern.description": "Rails, React, TypeScript, Inertia.js, and TailwindCSS working in perfect harmony.",
    "landing.features.production.title": "Production Ready",
    "landing.features.production.description": "Includes user management, sessions, email verification, and role-based access control.",
    "landing.footer.copyright": "Social Toolkit",
    "landing.footer.github": "GitHub",
    "landing.footer.contact": "Contact",

    // Pricing
    "pricing.title": "Plans & Pricing",
    "pricing.subtitle": "Choose the perfect plan for your needs",
    "pricing.per_month": "/month",
    "pricing.per_year": "/year",
    "pricing.one_time": "one-time",
    "pricing.popular": "Most popular",
    "pricing.get_started": "Get Started",
    "pricing.secure_payments": "Secure payments powered by Stripe",

    // Sidebar
    "sidebar.platform": "Platform",
    "sidebar.admin": "Admin",
    "sidebar.plans": "Plans",
    "sidebar.searches": "Searches",
    "sidebar.insights": "Insights",
    "sidebar.billing": "Billing",

    // Admin Plans
    "admin.plans.title": "Payment Plans",
    "admin.plans.subtitle": "Manage subscription plans and pricing",
    "admin.plans.new": "New Plan",
    "admin.plans.list": "Plans List",
    "admin.plans.list_description": "All configured payment plans",
    "admin.plans.empty": "No plans configured. Create your first plan.",
    "admin.plans.name": "Name",
    "admin.plans.price": "Price",
    "admin.plans.interval": "Interval",
    "admin.plans.status": "Status",
    "admin.plans.stripe_id": "Stripe Price ID",
    "admin.plans.actions": "Actions",
    "admin.plans.active": "Active",
    "admin.plans.inactive": "Inactive",
    "admin.plans.monthly": "Monthly",
    "admin.plans.yearly": "Yearly",
    "admin.plans.one_time": "One-time",
    "admin.plans.delete_confirm_title": "Delete plan?",
    "admin.plans.delete_confirm_description": "Are you sure you want to delete the plan {name}? This action cannot be undone.",
    "admin.plans.edit": "Edit Plan",
    "admin.plans.create": "Create Plan",
    "admin.plans.description": "Description",
    "admin.plans.stripe_price_id": "Stripe Price ID",
    "admin.plans.stripe_product_id": "Stripe Product ID",
    "admin.plans.price_cents": "Price (cents)",
    "admin.plans.currency": "Currency",
    "admin.plans.position": "Position",
    "admin.plans.features": "Features (one per line)",
    "admin.plans.save": "Save Plan",
    "sidebar.repository": "Repository",
    "sidebar.documentation": "Documentation",

    // Email
    "mailer.footer": "Have questions? Just reply to this email and our support team will help you out.",
    "mailer.password_reset.subject": "Reset your password",
    "mailer.email_verification.subject": "Verify your email",

    // Settings
    "settings.title": "Settings",
    "settings.subtitle": "Manage your profile and account settings",
    "settings.profile": "Profile",
    "settings.email": "Email",
    "settings.password": "Password",
    "settings.mcp_token": "MCP Token",
    "settings.sessions": "Sessions",
    "settings.billing": "Billing",
    "settings.appearance": "Appearance",
    "settings.profile.title": "Profile information",
    "settings.profile.subtitle": "Update your name and language preferences",
    "settings.profile.name": "Name",
    "settings.profile.locale": "Language",
    "settings.profile.locale_es": "Español (México)",
    "settings.profile.locale_en": "English (US)",
    "settings.profile.save": "Save",
    "settings.profile.saved": "Saved",
    "settings.email.title": "Email address",
    "settings.email.subtitle": "Update your email address",
    "settings.email.current": "Current email",
    "settings.email.new": "New email",
    "settings.email.save": "Save",
    "settings.password.title": "Update password",
    "settings.password.subtitle": "Ensure you use a secure password",
    "settings.password.current": "Current password",
    "settings.password.new": "New password",
    "settings.password.confirm": "Confirm password",
    "settings.password.save": "Save",
    "settings.sessions.title": "Active sessions",
    "settings.sessions.subtitle": "Manage your active sessions",
    "settings.sessions.current": "Active session",
    "settings.sessions.revoke": "Revoke session",
    "settings.appearance.title": "Appearance",
    "settings.appearance.subtitle": "Customize the app appearance",
    "settings.appearance.theme": "Theme",
    "settings.appearance.light": "Light",
    "settings.appearance.dark": "Dark",
    "settings.appearance.system": "System",
    "settings.delete.title": "Delete account",
    "settings.delete.subtitle": "Delete your account and all of its resources",
    "settings.delete.button": "Delete account",
    "settings.delete.warning": "Warning",
    "settings.delete.warning_text": "Please proceed with caution, this cannot be undone.",
    "settings.delete.dialog_title": "Are you sure you want to delete your account?",
    "settings.delete.dialog_description": "Once your account is deleted, all of its resources and data will also be permanently deleted. Please enter your password to confirm you would like to permanently delete your account.",
    "settings.delete.password_label": "Password",
    "settings.delete.password_placeholder": "Password",
    "settings.delete.cancel": "Cancel",
    "settings.billing.title": "Billing & Subscription",
    "settings.billing.subtitle": "Manage your subscription and payment history",
    "settings.billing.current_plan": "Current Plan",
    "settings.billing.current_plan_description": "Your active subscription plan",
    "settings.billing.payment_history": "Payment History",
    "settings.billing.payment_history_description": "View your past payments and invoices",
    "settings.billing.no_plan": "You don't have an active plan",
    "settings.billing.view_plans": "View Available Plans",
    "settings.billing.manage_billing": "Manage Billing",
    "settings.billing.cancel_subscription": "Cancel Subscription",
    "settings.billing.cancel_confirm": "Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.",
    "settings.billing.cancel_success": "Your subscription has been scheduled for cancellation at the end of the billing period.",
    "settings.billing.cancel_error": "Failed to cancel subscription. Please try again.",
    "settings.billing.no_subscription": "You don't have an active subscription to cancel.",
    "settings.billing.subscription_canceled": "Your subscription will be canceled at the end of the billing period.",
    "settings.billing.cancel_failed": "Failed to cancel subscription: %{error}",
    "settings.billing.payment": "Payment",
    "settings.billing.no_payments": "No payment history available",
    "settings.billing.monthly": "per month",
    "settings.billing.yearly": "per year",
    "settings.billing.one_time": "one-time",
    "settings.billing.stripe_info": "Billing is managed through Stripe. Click 'Manage Billing' to update payment methods or view invoices.",
    "settings.billing.status.active": "active",
    "settings.billing.status.succeeded": "succeeded",
    "settings.billing.status.canceled": "canceled",
    "settings.billing.status.past_due": "past due",
    "settings.billing.status.incomplete": "incomplete",
    "settings.billing.status.canceling": "canceling",
    "settings.billing.status.one_time": "one-time",
    "settings.mcp_token.title": "MCP Token",
    "settings.mcp_token.subtitle": "Manage your MCP access token",
    "settings.mcp_token.your_token": "Your Token",
    "settings.mcp_token.show": "Show",
    "settings.mcp_token.hide": "Hide",
    "settings.mcp_token.copy": "Copy",
    "settings.mcp_token.copied": "Copied!",
    "settings.mcp_token.regenerate": "Regenerate Token",
    "settings.mcp_token.regenerated": "Token regenerated successfully",
    "settings.mcp_token.regenerate_confirm": "Are you sure? Your old token will stop working immediately.",
    "settings.mcp_token.last_access": "Last access",
    "settings.mcp_token.never_used": "Never used",
    "settings.mcp_token.just_now": "Just now",
    "settings.mcp_token.minutes_ago": "%{count} minutes ago",
    "settings.mcp_token.hours_ago": "%{count} hours ago",
    "settings.mcp_token.days_ago": "%{count} days ago",
    "settings.mcp_token.how_to_use": "How to use your MCP token",
    "settings.mcp_token.instructions": "Use this token to connect Claude, Cursor, or other MCP clients to your account. Add the URL below to your MCP client:",

    // Auth
    "auth.sign_in": "Sign in",
    "auth.sign_up": "Sign up",
    "auth.sign_out": "Sign out",
    "auth.forgot_password": "Forgot your password?",
    "auth.no_account": "Don't have an account?",
    "auth.have_account": "Already have an account?",
    "auth.login.title": "Log in to your account",
    "auth.login.description": "Enter your email and password below to log in",
    "auth.login.email": "Email address",
    "auth.login.email_placeholder": "email@example.com",
    "auth.login.password": "Password",
    "auth.login.password_placeholder": "Password",
    "auth.login.button": "Log in",
    "auth.signup.title": "Create an account",
    "auth.signup.description": "Enter your details below to create your account",
    "auth.signup.name": "Name",
    "auth.signup.name_placeholder": "Full name",
    "auth.signup.email": "Email address",
    "auth.signup.email_placeholder": "email@example.com",
    "auth.signup.password": "Password",
    "auth.signup.password_placeholder": "Password",
    "auth.signup.password_confirmation": "Confirm password",
    "auth.signup.password_confirmation_placeholder": "Confirm password",
    "auth.signup.button": "Create account",
    "auth.password_reset.title": "Forgot password",
    "auth.password_reset.description": "Enter your email to receive a password reset link",
    "auth.password_reset.email": "Email address",
    "auth.password_reset.email_placeholder": "email@example.com",
    "auth.password_reset.button": "Email password reset link",
    "auth.password_reset.return": "Or, return to",
    "auth.password_reset.login": "log in",

  },
}

let currentLocale = "es-MX"

export function setLocale(locale: string) {
  if (translations[locale]) {
    currentLocale = locale
  }
}

export function getLocale(): string {
  return currentLocale
}

export function t(key: string, params?: Record<string, string | number>): string {
  const localeTranslations = translations[currentLocale] || translations["en-US"]
  let translation = (localeTranslations[key] as string) || key

  if (params) {
    Object.entries(params).forEach(([paramKey, value]) => {
      translation = translation.replace(`%{${paramKey}}`, String(value))
    })
  }

  return translation
}

export function getAvailableLocales(): string[] {
  return Object.keys(translations)
}
